import React, { useEffect, useState } from 'react';
import { FieldArray, Formik, FormikValues, yupToFormErrors } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Button, Form, Toast } from 'react-bootstrap';
import classNames from 'classnames';
import Plus from 'mdi-react/PlusIcon';
import Minus from 'mdi-react/CloseIcon';
import { helper } from 'utils/helper';
import { IAutoApproveSetting } from 'utils/commonTypes';
import TopBorderBtn from '../../../shared/TopBorderBtn';
import YesNoModal from '../../../shared/YesNoModal';
import { useModal } from '../../../shared/hooks';
import styles from './DirectorSettings.module.scss';
import TitleUndescore from '../../../shared/TitleUndescore';
import Picker from './Picker';
import { getSettingsRequest, postSettingsRequest } from './service/actions';
import { Checkbox, Input, MaskInput, Select } from '../../../shared/Forms';
import { logoutSelector } from '../../../common/LogoutBtn/selectors';
import { RootState } from '../../../../rootReducer';
import validationSchema from './service/schema';
import ErrorTime from './service/ErrorTime';

const DirectorSettings = () => {
  const dispatch = useDispatch();

  const { branchTitle } = useSelector(logoutSelector);
  const { isShow: isShowCloseTab, openModal: openCloseTab, closeModal: closeCloseTab } = useModal();
  const [errorTimeView, setErrorTimeView] = useState(false);
  const [errorTimeCell, setErrorTimeCell] = useState(false);
  const [error, setError] = useState('');

  const settings = useSelector((state: RootState) => state.directorModuleReducer.directorSettingsReducer.settings);

  const autoApproveInitialList: IAutoApproveSetting[] = [];

  for (let i = 0; i <= 3; i += 1) {
    autoApproveInitialList.push({
      isActive: false,
      positionsFrom: 0,
      positionsTo: 0,
      timeToWaitMin: 0,
    });
  }

  const timeOptions: number[] = [];

  for (let i = 60; i <= 600; i += 15) {
    timeOptions.push(i);
  }

  const initValue: any = {
    id: settings?.cellSettings?.id,
    startVisible: moment(settings?.cellSettings?.startVisible),
    endVisible: moment(settings?.cellSettings?.endVisible),
    startWeekendBookable: moment(settings?.cellSettings?.startWeekendBookable),
    endWeekendBookable: moment(settings?.cellSettings?.endWeekendBookable),
    startBookable: moment(settings?.cellSettings?.startBookable),
    endBookableTime: moment(settings?.cellSettings?.endBookable),
    nextDayBookingBanBeginTime: moment(settings?.cellSettings?.nextDayBookingBanBeginTime),
    slotDurationInMinutes: Number(settings?.cellSettings?.slotDurationInMinutes),
    maxReservationCount: Number(settings?.cellSettings?.maxReservationCount),
    bookMattersMin: Number(settings?.cellSettings?.bookMattersMin),
    bookArriveCorridor: settings?.cellSettings?.bookArriveCorridor,
    blockNextDay: moment(),
    autoApproveTime: moment(),
    autoApproveList:
      settings?.cellSettings?.autoApproves && settings?.cellSettings?.autoApproves?.length > 0
        ? settings.cellSettings.autoApproves
        : autoApproveInitialList,
    delayMinutesToInformBySms: settings.cellSettings?.delayMinutesToInformBySms,
    twoPosition: false,
    onePosition: false,
    threePosition: false,
    fourPosition: false,
    requestTimeBook2: 1,
    requestTimeBook3: 1,
    requestTimeBook4: 1,
    directorList:
      settings?.directors?.length === 0
        ? [
            {
              firstName: '',
              middleName: '',
              lastName: '',
              phone: { id: 0, number: '' },
            },
          ]
        : settings?.directors,
    tollerance: null,
    workingDays: settings?.cellSettings?.workingDays,
  };

  const saveSettings = (values: any) => {
    let arr = values?.maxReservationCount;
    if (typeof values?.maxReservationCount === 'string') {
      const splt = values?.maxReservationCount.split('_');
      // eslint-disable-next-line prefer-destructuring
      arr = splt[0];
    }

    const body = {
      cellSettings: {
        id: values.id,
        branchId: branchTitle?.id,
        bookArriveCorridor: values?.bookArriveCorridor,
        startVisible: values?.startVisible?.format('YYYY-MM-DDTHH:mm:00'),
        endVisible: values?.endVisible?.format('YYYY-MM-DDTHH:mm:00'),
        startBookable: values?.startBookable?.format('YYYY-MM-DDTHH:mm:00'),
        endBookable: values?.endBookableTime?.format('YYYY-MM-DDTHH:mm:00'),
        // startWeekendBookable: values?.startWeekendBookable?.format('YYYY-MM-DDTHH:mm:00'),
        // endWeekendBookable: values?.endWeekendBookable?.format('YYYY-MM-DDTHH:mm:00'),
        nextDayBookingBanBeginTime: values?.nextDayBookingBanBeginTime?.format('YYYY-MM-DDTHH:mm:00'),
        slotDurationInMinutes: Number(values?.slotDurationInMinutes),
        bookMattersMin: Number(values?.bookMattersMin),
        maxReservationCount: Number(arr),
        autoApproves: values?.autoApproveList.map((item: IAutoApproveSetting) => ({
          ...item,
          timeToWaitMin: typeof item.timeToWaitMin === 'string' ? parseInt(item.timeToWaitMin, 10) : item.timeToWaitMin,
        })),
        delayMinutesToInformBySms: parseInt(values.delayMinutesToInformBySms, 10),
        workingDays: values?.workingDays,
      },
      directors: values.directorList,
    };
    if (
      moment(body.cellSettings.startVisible).unix() > moment(body.cellSettings.startBookable).unix() ||
      moment(body.cellSettings.endBookable).unix() > moment(body.cellSettings.endVisible).unix()
      // ||  moment(body.cellSettings.startVisible).unix() > moment(body.cellSettings.startWeekendBookable).unix() ||
      // moment(body.cellSettings.endWeekendBookable).unix() > moment(body.cellSettings.endVisible).unix()
    ) {
      setError('Область відображення повинна перевищувати область бронювання');
      setTimeout(() => setError(''), 7000);
    } else {
      dispatch(postSettingsRequest(body));
    }
  };

  useEffect(() => {
    dispatch(getSettingsRequest());
  }, []);

  const validate = (v: any) => {
    try {
      validationSchema.validateSync(v, { abortEarly: false, context: { values: v } });
      return {};
    } catch (err) {
      // @ts-igonre
      console.log((err as any)?.errors);
      return yupToFormErrors<FormikValues>(err);
    }
  };

  return (
    <>
      {Object.keys(settings).length !== 0 && (
        <Formik
          initialValues={initValue}
          onSubmit={(values: any) => saveSettings(values)}
          validationSchema={validationSchema}
          validate={validate}
        >
          {({ handleChange, values, isValid }) => (
            <form>
              <section className={styles.header}>
                <ErrorTime setErrorTimeView={setErrorTimeView} setErrorTimeCell={setErrorTimeCell} />
                <h3 className="font-weight-bolder m-0">Налаштування</h3>
                <TopBorderBtn
                  icon="save"
                  disabled={errorTimeView || errorTimeCell || !isValid || values?.maxReservationCount === 0}
                  clickHandler={() => {
                    openCloseTab();
                  }}
                />
              </section>

              <section className={styles.divider}>
                <TitleUndescore title="Налаштування бронювання" />
              </section>

              <section className="pl-4 mb-3">
                <div className="d-flex flex-row align-items-center">
                  <h5 className={styles.staticText}>Область відобаження, час доби:</h5>
                  <div className={styles.timeTracker}>
                    <div className="d-flex align-items-center flex-row">
                      <h5 className="mr-2">з:</h5>
                      <Picker name="startVisible" value={settings?.cellSettings?.startVisible} />
                    </div>
                    <div className="d-flex align-items-center flex-row">
                      <h5 className="mr-2">по:</h5>
                      <Picker name="endVisible" value={settings?.cellSettings?.endVisible} />
                    </div>
                  </div>
                  {errorTimeView && <h5 className="text-danger">Час введено некорректно</h5>}
                </div>
              </section>

              <section className="pl-4 mb-3">
                <div className="d-flex flex-row align-items-center">
                  <h5 className={styles.staticText}>Область бронювання, час доби:</h5>
                  <div className={styles.timeTracker}>
                    <div className="d-flex align-items-center flex-row">
                      <h5 className="mr-2">з:</h5>
                      <Picker name="startBookable" value={settings?.cellSettings?.startBookable} />
                    </div>
                    <div className="d-flex align-items-center flex-row">
                      <h5 className="mr-2">по:</h5>
                      <Picker name="endBookableTime" value={settings?.cellSettings?.endBookable} />
                    </div>
                  </div>
                  {errorTimeCell && <h5 className="text-danger">Час введено некорректно</h5>}
                </div>
              </section>

              <section className="d-flex flex-row pl-4 mb-1">
                <h5 className={styles.staticText}>Тривалість слота, хв:</h5>
                <div className={styles.inputWrapper}>
                  <Select
                    name="slotDurationInMinutes"
                    label=""
                    onChange={handleChange}
                    value={values?.slotDurationInMinutes}
                    className={styles.inputSmall}
                  >
                    <option>15</option>
                    <option>30</option>
                    <option>45</option>
                    <option>60</option>
                  </Select>
                </div>
              </section>

              <section className="d-flex flex-row pl-4 mb-1">
                <h5 className={styles.staticText}>Ємність слота, рейсів, шт:</h5>
                <div className={styles.inputWrapper}>
                  <Input
                    name="maxReservationCount"
                    type="number"
                    className={styles.inputSmall}
                    maxLength={2}
                    label=""
                    onChange={handleChange}
                    value={values?.maxReservationCount}
                  />
                </div>
              </section>

              <section className="d-flex flex-row pl-4 mb-3">
                <h5 className={styles.staticText}>Допустиме запізнення рейсу з бронюванням, хв:</h5>
                <div className={styles.inputWrapper}>
                  <Input
                    name="bookArriveCorridor"
                    type="number"
                    className={styles.inputSmall}
                    maxLength={3}
                    label=""
                    onChange={handleChange}
                    value={values?.bookArriveCorridor}
                  />
                </div>
              </section>

              <section className="d-flex flex-row pl-4 mb-3">
                <h5 className={styles.staticText}>Час до настання бронювання хв:</h5>
                <div className={styles.inputWrapper}>
                  <Input
                    name="bookMattersMin"
                    type="number"
                    className={styles.inputSmall}
                    maxLength={3}
                    label=""
                    onChange={handleChange}
                    value={values?.bookMattersMin}
                  />
                </div>
              </section>

              {/* <section className="pl-4 mb-3">
                <div className="d-flex flex-row align-items-center">
                  <h5 className={styles.staticText}>Область бронювання у вихідний день:</h5>
                  <div className={styles.timeTracker}>
                    <div className="d-flex align-items-center flex-row">
                      <h5 className="mr-2">з:</h5>
                      <Picker name="startWeekendBookable" value={values.startWeekendBookable} />
                    </div>
                    <div className="d-flex align-items-center flex-row">
                      <h5 className="mr-2">по:</h5>
                      <Picker name="endWeekendBookable" value={values.endWeekendBookable} />
                    </div>
                  </div>
                  {errorTimeView && <h5 className="text-danger">Час введено не корректно</h5>}
                </div>
              </section> */}

              <section className="pl-4 mb-3">
                <div className="d-flex flex-row align-items-center">
                  <h5 className={styles.staticText}>Заблокувати час можливості реєстрації на наступний день:</h5>
                  <div className={styles.timeTracker}>
                    <div className="d-flex align-items-center flex-row">
                      <h5 className="mr-2">з:</h5>
                      <Picker name="nextDayBookingBanBeginTime" value={values.nextDayBookingBanBeginTime} />
                    </div>
                  </div>
                  {errorTimeView && <h5 className="text-danger">Час введено не корректно</h5>}
                </div>
              </section>

              <section className="pl-4 mb-3">
                <div className="d-flex flex-row align-items-center">
                  <h5 className={styles.staticText}>Автоматичне підтвердження нерозглянутих бронювань:</h5>

                  <table className={`table ${styles.AutoApproveTable}`}>
                    <thead>
                      <tr>
                        <th>Увімк/Вимк</th>
                        <th>Кількість позицій від</th>
                        <th>Кількість позицій до</th>
                        <th>Тривалість очікування, хв</th>
                      </tr>
                    </thead>

                    <tbody>
                      {values?.autoApproveList.map(
                        (_approveItem: IAutoApproveSetting, i: number, array: IAutoApproveSetting[]) => (
                          <tr>
                            <td>
                              <div className={styles.AutoApproveCell}>
                                <Checkbox
                                  name={`autoApproveList[${i}].isActive`}
                                  label=""
                                  className={styles.checkbox}
                                  onChange={handleChange}
                                  value={values.autoApproveList[i].isActive}
                                />
                              </div>
                            </td>

                            <td>
                              <div className={styles.AutoApproveCell}>
                                <Input
                                  name={`autoApproveList[${i}].positionsFrom`}
                                  type="number"
                                  className={styles.inputSmall}
                                  maxLength={3}
                                  onChange={handleChange}
                                  isPosNumber
                                  disabled={i === 0 || !values.autoApproveList[i].isActive}
                                />
                              </div>
                            </td>

                            <td>
                              <div className={styles.AutoApproveCell}>
                                <Input
                                  name={`autoApproveList[${i}].positionsTo`}
                                  type="number"
                                  className={styles.inputSmall}
                                  maxLength={3}
                                  onChange={handleChange}
                                  disabled={i === array.length - 1 || !values.autoApproveList[i].isActive}
                                />
                              </div>
                            </td>

                            {i === 0 ? (
                              <td>
                                <div className={styles.AutoApproveCell}>
                                  <span className={styles.AutoApproveLabel}>Миттєве погодження</span>
                                </div>
                              </td>
                            ) : (
                              <td>
                                <div className={styles.AutoApproveCell}>
                                  <div className={styles.inputWrapper}>
                                    <Select
                                      name={`autoApproveList[${i}].timeToWaitMin`}
                                      label=""
                                      onChange={handleChange}
                                      value={values?.slotDurationInMinutes}
                                      className={styles.inputSmall}
                                      disabled={!values.autoApproveList[i].isActive}
                                    >
                                      {timeOptions.map((timeOption) => (
                                        <option value={timeOption}>
                                          {timeOption} ({helper.convertMinutesToString(timeOption)})
                                        </option>
                                      ))}
                                    </Select>
                                  </div>
                                </div>
                              </td>
                            )}
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="pl-4 mb-3">
                <div className="d-flex flex-row align-items-center">
                  <h5 className={styles.staticText}>
                    Кількість хвилин очікування для відправлення СМС про авто в черзі на завантаження
                  </h5>

                  <div className={styles.inputWrapper}>
                    <Input
                      name="delayMinutesToInformBySms"
                      type="number"
                      className={styles.inputSmall}
                      maxLength={3}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </section>

              <section className="pl-4 mb-3">
                <div className="d-flex flex-row align-items-center">
                  <h5 className={styles.staticText}>Робочі дні:</h5>
                  <div className={styles.workingDays}>
                    <div className="d-flex mr-3">
                      <span>Пн</span>
                      <Checkbox
                        name="workingDays.isMondayWorking"
                        label=""
                        className={styles.checkbox}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="d-flex mr-3">
                      <span>Вт</span>
                      <Checkbox
                        name="workingDays.isTuesdayWorking"
                        label=""
                        className={styles.checkbox}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="d-flex mr-3">
                      <span>Ср</span>
                      <Checkbox
                        name="workingDays.isWednesdayWorking"
                        label=""
                        className={styles.checkbox}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="d-flex mr-3">
                      <span>Чт</span>
                      <Checkbox
                        name="workingDays.isThursdayWorking"
                        label=""
                        className={styles.checkbox}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="d-flex mr-3">
                      <span>Пт</span>
                      <Checkbox
                        name="workingDays.isFridayWorking"
                        label=""
                        className={styles.checkbox}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="d-flex mr-3">
                      <span>Сб</span>
                      <Checkbox
                        name="workingDays.isSaturdayWorking"
                        label=""
                        className={styles.checkbox}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="d-flex mr-3">
                      <span>Нд</span>
                      <Checkbox
                        name="workingDays.isSundayWorking"
                        label=""
                        className={styles.checkbox}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </section>
              {/* <section className="pl-4 mb-5"> */}
              {/*  <div className="d-flex flex-row align-items-center"> */}
              {/*    <h5 className={styles.staticText}> */}
              {/*      Час до настання бронювання хв, протягом якого рейс буде показано в черзі SMC Mobile як рейс з */}
              {/*      /!* eslint-disable-next-line react/no-unescaped-entities *!/ */}
              {/*      пріоритетом 'Бронь на': */}
              {/*    </h5> */}
              {/*    <div className={styles.timeTracker}> */}
              {/*      <Input */}
              {/*        name="name" */}
              {/*        type="number" */}
              {/*        className={styles.inputSmall} */}
              {/*        maxLength={3} */}
              {/*        label="" */}
              {/*        onChange={handleChange} */}
              {/*      /> */}
              {/*    </div> */}
              {/*  </div> */}
              {/* </section> */}

              {/* <section className={styles.divider}> */}
              {/*  <TitleUndescore title="Автоматичне підтвердження бронювання" /> */}
              {/* </section> */}
              {/* <section className="pl-4 mb-3"> */}
              {/*  <div className="d-flex flex-row align-items-center"> */}
              {/*    <h5 className={styles.staticText}>Якщо кількість бронювання =1:</h5> */}
              {/*    <div className={styles.timeTracker}> */}
              {/*      <Checkbox name="onePosition" label="" className={styles.checkbox} onChange={handleChange} /> */}
              {/*    </div> */}
              {/*  </div> */}
              {/* </section> */}
              {/* <section className="pl-4 mb-3"> */}
              {/*  <div className="d-flex flex-row align-items-center"> */}
              {/*    <h5 className={styles.staticText}>Якщо кількість бронювання =2:</h5> */}
              {/*    <div className={styles.timeTracker}> */}
              {/*      <Checkbox name="twoPosition" label="" className={styles.checkbox} onChange={handleChange} /> */}
              {/*      <div className="d-flex ml-2 align-items-center flex-row"> */}
              {/*        <h5 className="mr-2 text-nowrap">Час погодження, год:</h5> */}
              {/*        <Select */}
              {/*          name="requestTimeBook2" */}
              {/*          label="" */}
              {/*          onChange={handleChange} */}
              {/*          value={values?.requestTimeBook2} */}
              {/*          className={styles.inputSmall} */}
              {/*          onLine */}
              {/*        > */}
              {/*          <option>1</option> */}
              {/*          <option>2</option> */}
              {/*          <option>3</option> */}
              {/*          <option>4</option> */}
              {/*          <option>5</option> */}
              {/*          <option>6</option> */}
              {/*        </Select> */}
              {/*      </div> */}
              {/*    </div> */}
              {/*  </div> */}
              {/* </section> */}
              {/* <section className="pl-4 mb-3"> */}
              {/*  <div className="d-flex flex-row align-items-center"> */}
              {/*    <h5 className={styles.staticText}>Якщо кількість бронювання =3:</h5> */}
              {/*    <div className={styles.timeTracker}> */}
              {/*      <Checkbox name="threePosition" label="" className={styles.checkbox} onChange={handleChange} /> */}
              {/*      <div className="d-flex ml-2 align-items-center flex-row"> */}
              {/*        <h5 className="mr-2 text-nowrap">Час погодження, год:</h5> */}
              {/*        <Select */}
              {/*          name="requestTimeBook3" */}
              {/*          label="" */}
              {/*          onChange={handleChange} */}
              {/*          value={values?.requestTimeBook2} */}
              {/*          className={styles.inputSmall} */}
              {/*          onLine */}
              {/*        > */}
              {/*          <option>1</option> */}
              {/*          <option>2</option> */}
              {/*          <option>3</option> */}
              {/*          <option>4</option> */}
              {/*          <option>5</option> */}
              {/*          <option>6</option> */}
              {/*        </Select> */}
              {/*      </div> */}
              {/*    </div> */}
              {/*  </div> */}
              {/* </section> */}
              {/* <section className="pl-4"> */}
              {/*  <div className="d-flex mb-5 flex-row align-items-center"> */}
              {/*    <h5 className={styles.staticText}>Якщо кількість бронювання =4:</h5> */}
              {/*    <div className={styles.timeTracker}> */}
              {/*      <Checkbox name="fourPosition" label="" className={styles.checkbox} onChange={handleChange} /> */}
              {/*      <div className="d-flex ml-2 align-items-center flex-row"> */}
              {/*        <h5 className="mr-2 text-nowrap">Час погодження, год:</h5> */}
              {/*        <Select */}
              {/*          name="requestTimeBook4" */}
              {/*          label="" */}
              {/*          onChange={handleChange} */}
              {/*          value={values?.requestTimeBook2} */}
              {/*          className={styles.inputSmall} */}
              {/*          onLine */}
              {/*        > */}
              {/*          <option>1</option> */}
              {/*          <option>2</option> */}
              {/*          <option>3</option> */}
              {/*          <option>4</option> */}
              {/*          <option>5</option> */}
              {/*          <option>6</option> */}
              {/*        </Select> */}
              {/*      </div> */}
              {/*    </div> */}
              {/*  </div> */}
              {/* </section> */}

              <section className={styles.divider}>
                <TitleUndescore title="Завсклад" />
              </section>
              <section className="pl-4 mb-3">
                <FieldArray
                  name="directorList"
                  render={(arrayHelpers) => {
                    const addHandler = () =>
                      arrayHelpers.push({
                        firstName: '',
                        middleName: '',
                        lastName: '',
                        phone: { id: 0, number: '' },
                      });
                    const removeHandler = (index: number) => arrayHelpers.remove(index);

                    return (
                      <div className="d-flex flex-column w-100">
                        {values.directorList?.map((v: any, index: number) => (
                          <div className={styles.directorListWrapper}>
                            <div className={styles.directorListHeader}>
                              {index === 0 ? (
                                <Button
                                  variant="success"
                                  className={classNames(
                                    styles.plus_btn,
                                    'd-flex justify-content-center align-items-center mr-2 p-1 rounded-circle',
                                  )}
                                  onClick={() => addHandler()}
                                >
                                  <Plus size={14} />
                                </Button>
                              ) : (
                                <Button
                                  variant="danger"
                                  className={classNames(
                                    styles.plus_btn,
                                    'd-flex justify-content-center align-items-center mr-2 p-1 rounded-circle',
                                  )}
                                  onClick={() => removeHandler(index)}
                                >
                                  <Minus size={14} />
                                </Button>
                              )}
                              <Form.Label>Завсклад {index + 1}</Form.Label>
                            </div>
                            <div className={styles.directorListBody} key={index}>
                              <Input
                                name={`directorList[${index}].lastName`}
                                label="Прізвище"
                                onChange={handleChange}
                                maxLength={40}
                                value={v?.lastName}
                              />
                              <Input
                                name={`directorList[${index}].firstName`}
                                label="Ім'я"
                                onChange={handleChange}
                                maxLength={40}
                                value={v?.firstName}
                              />
                              <Input
                                name={`directorList[${index}].middleName`}
                                label="По батькові"
                                onChange={handleChange}
                                maxLength={40}
                                value={v?.middleName}
                              />
                              <MaskInput
                                name={`directorList[${index}].phone.number`}
                                label="Телефон"
                                placeholder="38 (0XX) XXX-XX-XX"
                                onChange={handleChange}
                                value={v?.phone?.number}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }}
                />
              </section>
              <YesNoModal
                isShow={isShowCloseTab}
                title="Бажаєте зберегти зміни"
                closeHandler={closeCloseTab}
                acceptHandler={() => {
                  saveSettings(values);
                  closeCloseTab();
                }}
                closeModal={() => {
                  closeCloseTab();
                }}
              />
            </form>
          )}
        </Formik>
      )}

      <div className={classNames('p-3', styles.toast)}>
        <Toast show={error !== ''} delay={7000} className={styles.errorBody}>
          <Toast.Header>
            <strong className="text-danger">Помилка!</strong>
          </Toast.Header>
          <Toast.Body>{error}</Toast.Body>
        </Toast>
      </div>
    </>
  );
};

export default DirectorSettings;
